import blogServices from 'src/services/blog'
import * as cheerio from 'cheerio'
import slugify from 'slugify'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import DefaultLayout from '@/layouts/default'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { decode } from 'html-entities'
import ShareLink from '@/components/pages/blog/ShareLink'
import { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import ReleatedPost from '@/components/pages/blog/RelatedPost'
// import ModalSubscribe from '@/components/pages/blog/ModalSubscribe'
import IconChevronRight from '@/public/icons/chevron-right.svg'
import FormSubscribe from '@/components/pages/blog/FormSubscribe'
import CTA from '@/components/pages/blog/CTA.tsx'
import { useRouter } from 'next/router'
import Head from 'next/head'
import Image from 'next/image'
import {
  postDetailDescriptionRegex,
  postDetailOgDescriptionRegex,
  postDetailOgTitleRegex,
  postDetailPageTitleRegex,
  postDetailTitleRegex,
} from '@/config/constants'

// Server side
export async function getServerSideProps({ locale, params }) {
  try {
    const languageTags = await blogServices.fetchBlogTag({ slug: locale })
    const currentTagId = languageTags.data[0].id
    const paramsFetchPosts = {
      _embed: 1,
      slug: params.slug,
      head_tags: true,
    }
    const postsDetail = await blogServices.fetchBlogPost(paramsFetchPosts)
    if (!postsDetail.data.length) {
      return {
        props: {
          ...(await serverSideTranslations(locale)),
        },
        notFound: true,
      }
    }

    if (postsDetail.data[0].tags && !postsDetail.data[0].tags.includes(currentTagId)) {
      if (locale === 'vi') {
        return {
          redirect: {
            destination: `/blog/${postsDetail.data[0].slug}`,
            permanent: false,
          },
        }
      }
      return {
        redirect: {
          destination: `/vi/blog/${postsDetail.data[0].slug}`,
          permanent: false,
        },
      }
    }

    const $ = cheerio.load(postsDetail.data[0].content.rendered)
    const $_content = cheerio.load(postsDetail.data[0].content.rendered)
    const readingTime = $_content('.rt-reading-time .rt-time').text() || ''
    const tableContents = []
    $('h2, h3').each(function (i, e) {
      const title = $(e).text()
      const slug = slugify(title, { locale: 'vi', lower: true })
      tableContents[i] = {
        text: title,
        link: slug,
        className:
          $(e).prop('tagName').toLowerCase() === 'h2' ? 'ml-0 text-sm' : 'ml-[6px] text-sm',
      }
      $(this).attr('id', slug)
    })
    const wordCount = $('body')
      .text()
      .split(/\s+/)
      .filter((word) => word.length > 0).length

    // Script in header
    const scriptTags = postsDetail.data[0]?.head_tags.filter((item) => item.tag === 'script')

    // Featured img
    let featuredImage = ''
    try {
      featuredImage = postsDetail.data[0]?._embedded?.['wp:featuredmedia']?.[0]?.source_url
    } catch (error) {}
    if (!featuredImage) {
      const images = $('img').attr('src')
      if (images) {
        featuredImage = images.replace(/-[0-9]+x[0-9]+/g, '')
      }
    }

    // Process metadata in parallel with other operations
    let metaPromise = null

    if (postsDetail.data[0]?.link) {
      metaPromise = blogServices
        .fetchBlogHead({ url: postsDetail.data[0]?.link })
        .catch((error) => {
          console.warn('Error fetching blog head:', error.message)
          return null
        })
    }

    // Wait for meta data if it was requested
    let headPostDetail = null
    let metaDescription = null
    let metaTitle = null

    if (metaPromise) {
      headPostDetail = await metaPromise

      if (headPostDetail?.data?.head) {
        const htmlData = headPostDetail?.data?.head

        // Extract metadata using regex patterns
        const matchDescription = htmlData.match(postDetailDescriptionRegex)
        const matchOgDescription = htmlData.match(postDetailOgDescriptionRegex)
        metaDescription = matchDescription
          ? matchDescription[1]
          : matchOgDescription
            ? matchOgDescription[1]
            : null

        const matchTitle = htmlData.match(postDetailTitleRegex)
        const matchOgTitle = htmlData.match(postDetailOgTitleRegex)
        const matchPageTitle = htmlData.match(postDetailPageTitleRegex)
        metaTitle = matchTitle
          ? matchTitle[1]
          : matchOgTitle
            ? matchOgTitle[1]
            : matchPageTitle
              ? matchPageTitle[1]
              : null
      }
    }

    return {
      props: {
        ...(await serverSideTranslations(locale)),
        metaTitle,
        metaDescription,
        postsDetail: {
          ...postsDetail.data[0],
          readingTime,
          newContent: $.html(),
          scriptTags,
          featuredImage,
          wordCount,
        },
        tableContents,
      },
    }
  } catch (error) {
    console.log('error', error)
    return {
      redirect: {
        destination: `/blog`,
        permanent: false,
      },
    }
  }
}

// Client page
export default function BlogDetail({ metaTitle, metaDescription, postsDetail, tableContents }) {
  const { t } = useTranslation('blog')
  const [isOpenModalSubscribe, setIsOpenModalSubscribe] = useState(false)
  const router = useRouter()

  var localizedFormat = require('dayjs/plugin/localizedFormat')
  dayjs.extend(localizedFormat)
  const formatDate = (value) => {
    return router.locale === 'en'
      ? dayjs(value).format('LL').replace(',', '')
      : dayjs(value).format('DD/MM/YYYY')
  }

  const categories = t('categories', { returnObjects: true })
  const blogCategories = useMemo(() => {
    if (categories && categories?.length) {
      const _blogCategories = postsDetail.categories.map((c) => c.toString())
      return categories.filter((item) => _blogCategories.includes(item.id))
    }
    return []
  }, [categories, postsDetail])

  const postGenre = useMemo(() => {
    const category = postsDetail?._embedded?.['wp:term']?.[0]?.find(
      (item) => ![8, 11, 280, 281].includes(item.id)
    )
    switch (category?.id) {
      case 8: {
        return 'Business'
      }
      case 285:
      case 54: {
        return 'Technology'
      }
      default: {
        return category?.name || 'News'
      }
    }
  }, [postsDetail?._embedded?.['wp:term']])

  const addBlogScriptTag = (scriptTags) => {
    if (!scriptTags) return null
    return scriptTags.map((scriptTag, index) => (
      <script
        type={scriptTag?.attributes?.type}
        key={index}
        dangerouslySetInnerHTML={{ __html: decode(scriptTag.content) }}
      ></script>
    ))
  }

  // useEffect(() => {
  //   let timoutOpenSubscribe
  //   if (router.locale === 'vi' && postsDetail && postsDetail.categories.includes(281)) {
  //     timoutOpenSubscribe = setTimeout(() => {
  //       setIsOpenModalSubscribe(true)
  //     }, 10000)

  //   }
  //   return () => clearTimeout(timoutOpenSubscribe)
  // }, [])

  useEffect(() => {
    if (window.innerWidth > 1024) {
      const blogContent = document.querySelector('.main-blog-content')
      const boundingRect = blogContent.getBoundingClientRect()
      const rightOffset = window.innerWidth - boundingRect.right
      const rightValue = rightOffset - 250
      const sidebar = document.getElementById('sidebar')
      sidebar.style.setProperty('right', `${rightValue}px`, 'important')
      sidebar.style.setProperty('position', `fixed`, 'important')
    } else {
      sidebar.style.setProperty('position', `static`, 'important')
      sidebar.style.setProperty('padding-top', `40px`, 'important')
      sidebar.style.setProperty('padding-bottom', `40px`, 'important')
      sidebar.style.setProperty('border-bottom', `1px solid #E4E7EC`, 'important')
    }
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerWidth > 1024) {
        const blogContent = document.querySelector('.main-blog-content')
        const boundingRect = blogContent.getBoundingClientRect()
        const rightOffset = window.innerWidth - boundingRect.right
        const rightValue = rightOffset - 250
        const sidebar = document.getElementById('sidebar')
        if (boundingRect.bottom - sidebar.offsetHeight < 180) {
          sidebar.style.setProperty('position', `absolute`, 'important')
          sidebar.style.setProperty('top', `0`, 'important')
        } else {
          sidebar.style.setProperty('top', `180px`, 'important')
          sidebar.style.setProperty('right', `${rightValue}px`, 'important')
          sidebar.style.setProperty('position', `fixed`, 'important')
        }
      } else {
        sidebar.style.setProperty('position', `static`, 'important')
        sidebar.style.setProperty('padding-top', `40px`, 'important')
        sidebar.style.setProperty('padding-bottom', `40px`, 'important')
        sidebar.style.setProperty('border-bottom', `1px solid #E4E7EC`, 'important')
      }
    }
    window.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, [])
  return (
    <>
      <DefaultLayout title={metaTitle} desc={metaDescription}>
        <Head>
          <meta property="og:type" content="article" />
          {addBlogScriptTag(postsDetail?.scriptTags)}
          {postsDetail?.featuredImage && (
            <>
              <meta property="og:image" content={postsDetail.featuredImage} />
              <meta property="twitter:image" content={postsDetail.featuredImage} />
            </>
          )}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `
            {
              "@context":"http://schema.org",
              "@type":"Article",
              "mainEntityOfPage":{
                  "@type":"WebPage",
                  "id":"https://locker.io/blog/${router.query.slug[0]}"
              },
              "headline":"${decode(postsDetail?.title?.rendered)}",
              "image":"${postsDetail?.featuredImage}",
              "description":"${decode(postsDetail?.excerpt?.rendered.replace(/<[^>]*>/g, ''))}",
              "datePublished":"${postsDetail?.date}",
              "dateModified":"${postsDetail?.modified}",
              "genre":"${postGenre}",
              "wordcount":${postsDetail?.wordCount},
              "Author":{
                  "name":"${postsDetail?._embedded.author[0]?.name || 'CyStack Editor'}"
              },
              "Publisher":{
                  "id":"https://locker.io/#Organization",
                  "logo":{
                    "width":106,
                    "height":36,
                    "@type":"ImageObject",
                    "url":"https://locker.io/images/logo/black.svg"
                  }
              },
              "Editor":{
                  "name":"CyStack Editor"
              }
            }
            `,
            }}
          />
        </Head>

        <section className="section-container locker-blog lg:pt-20 pt-10 max-w-[800px] mx-auto">
          <div className="flex items-center mb-8 gap-3">
            <Link href={'/'}>
              <span className="text-sm font-medium text-[#475467] cursor-pointer hover:underline hover:text-primary-HOVER">
                Home
              </span>
            </Link>
            <IconChevronRight />
            <Link href={'/blog'}>
              <span className="text-sm font-medium text-[#475467] cursor-pointer hover:underline hover:text-primary-HOVER">
                Blog
              </span>
            </Link>
            <IconChevronRight />
            <span className="text-sm font-semibold text-primary line-clamp-1">
              {postsDetail?.title?.rendered || ''}
            </span>
          </div>
          <h1
            id="blog-title"
            className="text-display-md md:text-display-lg text-[#101828] font-semibold mb-6"
          >
            {decode(postsDetail?.title?.rendered) || ''}
          </h1>

          <div className="flex items-center gap-2 text-sm text-heading-desctiption font-medium">
            <Image
              src="/images/blog/clock.svg"
              alt="Locker blog reading time"
              height={14}
              width={14}
            />
            {postsDetail.readingTime || 0} {t('minutes_read')}
          </div>

          <div className="py-3 mt-8 border border-solid border-l-0 border-r-0 border-[#E4E7EC] flex justify-between">
            <div className="flex items-center posts-info">
              <img
                src={
                  postsDetail?._embedded?.author && postsDetail?._embedded.author[0]?.avatar_urls
                    ? postsDetail._embedded.author[0]?.avatar_urls['48'] ||
                      postsDetail._embedded.author[0]?.avatar_urls['96'] ||
                      postsDetail._embedded.author[0]?.avatar_urls['24'] ||
                      '/images/default-avatar.svg'
                    : '/images/default-avatar.svg'
                }
                className="object-cover w-10 h-10 rounded-full"
                alt="Locker Avatar"
              />
              <div className="ml-2">
                <p className="font-semibold text-[#344054] text-sm">
                  {postsDetail?._embedded.author[0]?.name || 'CyStack Editor'}
                </p>
                {postsDetail?.date ? (
                  <div className="meta flex items-center text-[12px] leading-6 text-[#000519]">
                    <span>{formatDate(postsDetail?.date)}</span>
                  </div>
                ) : null}
              </div>
            </div>
            <ShareLink />
          </div>
          <div
            id="sidebar"
            className="w-full !static lg:max-h-[calc(100vh-200px)] overflow-y-auto max-h-fit lg:w-[180px] top-[200px] h-fit mb-10 lg:mb-0 bg-white"
          >
            {tableContents?.length ? (
              <>
                <p className="text-md font-semibold mb-4 text-primary">
                  {t('table_content', { ns: 'common' })}
                </p>
                <div className="flex flex-col gap-[6px]">
                  {tableContents.map((item) => (
                    <Link key={item.link} href={`#${item.link}`}>
                      <p className={`${item.className || ''} font-medium text-sm py-1 w-fit`}>
                        {item.text}
                      </p>
                    </Link>
                  ))}
                </div>
              </>
            ) : null}
          </div>

          <div className="main-blog-content relative flex flex-col py-4">
            {postsDetail._embedded.author[0]?.description ? (
              <div className="border border-solid border-l-0 border-r-0 border-t-0 border-[#E4E7EC] pb-4">
                <p
                  dangerouslySetInnerHTML={{
                    __html:
                      postsDetail._embedded.author[0]?.description?.replace(/<[^>]*>/g, '') || '',
                  }}
                ></p>
              </div>
            ) : null}
            <div
              dangerouslySetInnerHTML={{ __html: postsDetail?.newContent || '' }}
              className="pt-8"
            />
          </div>
        </section>

        <section className="locker-blog">
          <ReleatedPost post={postsDetail} categories={blogCategories} />
          <section className=" py-12 lg:py-24 text-center bg-[#F9FAFB]">
            <div className="section-container max-w-[768px] mx-auto">
              <h2 className="text-display-md font-semibold text-[#101828] mb-5">
                {t('register.title')}
              </h2>
              <p className="text-xl text-[#475467] mb-8">{t('register.description')}</p>
              <div className="max-w-[480px] mx-auto ">
                <FormSubscribe horizontal blog={postsDetail} />
                <p className="text-sm text-[#475467] text-left mt-2">
                  {t('register.privacy')}{' '}
                  <Link href={'/'}>
                    <span className="underline hover:text-primary-HOVER lowercase text-[#475467]">
                      {t('privacy_policy')}.
                    </span>
                  </Link>
                </p>
              </div>
            </div>
          </section>
        </section>

        <CTA
          contentType={postsDetail?.categories.includes(281) ? 'sm' : 'pm'}
          content={postsDetail?.acf}
        />
      </DefaultLayout>

      {/* <ModalSubscribe
        isOpen={isOpenModalSubscribe}
        blog={postsDetail}
        onCloseModal={() => setIsOpenModalSubscribe(false)}
      /> */}
    </>
  )
}
